/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  ICON_VARIANT_SIGN_OUT,
  ICON_VARIANT_SYMBOL_SECURE,
  ICON_VARIANT_THEME_DARK,
  ICON_VARIANT_THEME_LIGHT,
  ICON_VARIANT_THEME_SYSTEM,
  ICON_VARIANT_USER_SETTINGS,
} from '@aglyn/shared-data-enums'
import {
  mdiAccountGroupOutline,
  mdiBookOpenVariant,
  mdiBugOutline,
  mdiCookieCogOutline,
  mdiCreditCardOutline,
  mdiLifebuoy,
  mdiOpenInNew,
} from '@aglyn/shared-data-mdi'
import { CONSENT_OPT_OUT_TITLE } from '@aglyn/aglyn/app-utils/consent-banner-ui'
import {
  isEnterpriseOrg,
  PLAN_LABELS,
  readOrgPlanComp,
  resolveEffectivePlan,
} from '@aglyn/aglyn'
import { AppLink, MdiIcon } from '@aglyn/shared-ui-jsx'
import {
  useUser,
  useUserName,
  useUserPhoto,
} from '@aglyn/tenant-feature-instance'
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useColorScheme } from '@mui/material/styles'
import { type ReactNode, Suspense, lazy, useState } from 'react'
import MemberAvatar from './member-avatar.component'
import { openVisitorConsentPanel } from './visitor-consent.component'
import { buildDocsUrl } from '../constants/docs-links'
import { buildRoute, Route } from '../constants/route-links'
import useCurrentOrg from '../hooks/use-current-org'
import { useIsStaff } from '../hooks/use-is-staff'
import { useOrgReach } from '../hooks/use-org-reach'
import { useOrgScope, useOrgSlug } from '../hooks/use-org-scope'
import { useUrlNamesOrg } from '../hooks/use-secondary-nav'

/**
 * The report form arrives with the first person who asks for it (AGL-2706).
 *
 * This menu is mounted by the app layout, so a static import put the dialog —
 * 20 KB of source, its own form state, validation and upload handling — into
 * the eager graph of every console page. It is also imported by
 * `manage/report-issue/page.tsx`, which is a separate client entry, so the
 * chunker emitted the whole component TWICE.
 *
 * A `lazy()` boundary is honest here only because nothing else in this module
 * touches the dialog: a static and a dynamic import of one module resolve to
 * one module, and the static one wins.
 */
const ReportIssueDialog = lazy(() => import('./report-issue-dialog.component'))

/**
 * Plans with nothing above them to sell (org-billing.types): `agency` tops the
 * self-serve ladder since pricing v3, and `enterprise` sits off it entirely
 * (custom-priced, AGL-1118). Pitching either an upgrade is nonsense — this
 * used to name only `advanced`, so both saw the CTA.
 */
const TOP_PLANS: ReadonlySet<string> = new Set(['agency', 'enterprise'])

/**
 * The account menu in the top bar (AGL-858), redesigned after Vercel's:
 * an identity header with a settings gear, right-aligned row icons, an inline
 * light/system/dark theme toggle (moved out of the top bar), an Upgrade CTA
 * that only shows below the top plan, and a footer naming the workspace the
 * org-scoped links act on. Self-contained — reads its own user/org/theme.
 */
export function UserMenu() {
  const { data: user } = useUser()
  // The stored photo only (AGL-1683). This asked for a Gravatar fallback,
  // which meant every signed-in page handed gravatar.com an MD5 of the
  // account's email; the initial below is drawn locally and needs no vendor.
  const userPhotoUrl = useUserPhoto()
  // Only a URL that names a workspace lets this menu name one (AGL-1130).
  // `useOrgSlug()` falls back to the user's first org so that org-less pages
  // still have somewhere to act; the footer and the Upgrade CTA are CLAIMS
  // about the workspace you are in, and in the staff console or Manage
  // Account they claimed one picked by a fallback. Blanked here, the org rows
  // fall to the jump page on their own — the same path they already took
  // before an org resolved — so they stay usable without asserting which
  // workspace they mean.
  const namesOrg = useUrlNamesOrg()
  const resolvedOrgSlug = useOrgSlug()
  const orgSlug = namesOrg ? resolvedOrgSlug : ''
  const { currentOrg } = useOrgScope()
  const { org, ready: orgReady } = useCurrentOrg()
  const isStaff = useIsStaff()
  // Team / Billing / Support are org pages (AGL-1032). This menu is org nav
  // as much as the tab strip is — leaving the rows here would send a site
  // collaborator to a page the guard immediately bounces them off.
  const { orgWide, ready: reachReady } = useOrgReach()
  // …and a page that names no workspace has no org for them to act on. AGL-1130
  // blanked `orgSlug` here so these rows stopped CLAIMING a workspace picked by
  // a fallback, but it left them rendered and pointing at the jump page — so on
  // the workspace chooser, Manage Account and the staff console, "Manage Team"
  // and "Billing" were live rows that just bounced you to `/`. A row that
  // cannot do what it says should not be offered; hidden here, and the
  // account-scoped rows below cover what those pages can actually reach.
  const showOrgLinks = namesOrg && (!reachReady || orgWide)
  const { mode, setMode } = useColorScheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // The dialog is rendered OUTSIDE the Popover (below), so closing the menu on
  // click does not unmount the form the person is typing into (AGL-2185).
  const [reporting, setReporting] = useState(false)
  // Latches on the first open and never clears, so the dialog stays mounted
  // through its own closing transition rather than being cut at `open=false`.
  const [reportMounted, setReportMounted] = useState(false)

  const close = () => setAnchorEl(null)
  const orgHome = orgSlug ? buildRoute(Route.ORG_HOME, { orgSlug }) : '/'
  // Org-scoped destinations fall back to the jump page before an org resolves,
  // exactly like the Billing link did in the old menu.
  const orgLink = (
    route: Route.MANAGE_TEAM | Route.MANAGE_BILLING | Route.MANAGE_SUPPORT,
  ) => (orgSlug ? buildRoute(route, { orgSlug }) : orgHome)

  // `useUserName`, not `user.displayName` (AGL-2486). That field is empty for
  // every SSO account — a tenant auth record carries no `displayName` at all —
  // so this menu was showing the EMAIL as the person's name, repeating it as
  // the address on the line below, and handing `memberInitials` an address
  // with no space in it, which yields ONE letter where the presence stack two
  // inches away showed two. The name was never missing: `users/{uid}` has held
  // `firstName`/`lastName` for these accounts the whole time.
  const resolvedName = useUserName()
  const name = resolvedName || 'Account'
  const email = user?.email ?? ''
  // The plan the workspace GETS (AGL-3034): a staff comp names a plan the
  // stored field does not, and a dead subscription's stored plan is not one
  // the workspace has. Named only when there is a plan to name — stored, or
  // granted by a comp — so an org that has never had one still shows none.
  const plan = org ? resolveEffectivePlan(org) : undefined
  const planLabel = isEnterpriseOrg(org)
    ? PLAN_LABELS.enterprise
    : plan && (org?.plan || readOrgPlanComp(org))
      ? (PLAN_LABELS[plan] ?? '')
      : ''
  // Only pitch an upgrade once the org doc has resolved (AGL-887) — while
  // it's loading (or the read failed) `plan` is undefined, which would show
  // the CTA to a top-plan workspace.
  // …and a collaborator cannot buy anything either — the CTA lands on the
  // Billing page they may not open (AGL-1032).
  // …and an org already reading as Enterprise (custom-priced or comped off a
  // lower base plan, AGL-1110) has nothing to buy either.
  const showUpgrade =
    Boolean(orgSlug) &&
    orgReady &&
    !TOP_PLANS.has(String(plan)) &&
    !isEnterpriseOrg(org) &&
    showOrgLinks

  const themeValue = mode ?? 'system'

  const row = (
    href: string,
    label: string,
    iconPath: string,
    opts?: { external?: boolean },
  ): ReactNode => (
    // ListItemButton, not MenuItem: these rows live in a Popover, and MUI's
    // MenuItem throws without a MenuList/Menu context around it (AGL-858).
    <ListItemButton
      component={opts?.external ? 'a' : AppLink}
      href={href}
      onClick={close}
      {...(opts?.external
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      sx={{ gap: 2, py: 0.75 }}
    >
      <ListItemText
        slotProps={{ primary: { variant: 'body2' } }}
        sx={{ my: 0 }}
      >
        {label}
      </ListItemText>
      <MdiIcon path={iconPath} fontSize="small" sx={{ color: 'text.secondary' }} />
    </ListItemButton>
  )

  /**
   * The same row, but it runs something instead of going somewhere (AGL-2185).
   * `row` only builds links; "Report an issue" opens a dialog, and rebuilding
   * it as a link to a page would put a form behind a navigation the reporter
   * has to come back from — losing the page they were on, which is the single
   * most useful thing the report carries.
   */
  const actionRow = (
    onClick: () => void,
    label: string,
    iconPath: string,
  ): ReactNode => (
    <ListItemButton onClick={onClick} sx={{ gap: 2, py: 0.75 }}>
      <ListItemText
        slotProps={{ primary: { variant: 'body2' } }}
        sx={{ my: 0 }}
      >
        {label}
      </ListItemText>
      <MdiIcon path={iconPath} fontSize="small" sx={{ color: 'text.secondary' }} />
    </ListItemButton>
  )

  return (
    <>
      <Tooltip title="Manage account">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          edge="end"
          aria-label="Manage account"
          aria-haspopup="menu"
          sx={{ p: 0.5 }}
        >
          {/* The SAME avatar the presence stack draws (AGL-2486). This was a
              single grey letter on the SDK's default grey, sitting inches from
              a coloured two-initial avatar of the same person — and for every
              SSO account, which has no picture at all, the grey letter was the
              whole identity. I like how we created the named avatars
              for no profile picture let's do the same. Seeded on the email
              rather than the display name so the colour survives a rename. */}
          <MemberAvatar
            name={name}
            email={email}
            photoURL={userPhotoUrl}
            size={30}
          />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              mt: 0.75,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              backgroundColor: 'surface.main',
              backgroundImage: 'none',
              boxShadow: '0px 8px 24px rgba(0,0,0,0.18)',
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Identity header + settings gear → Manage Account. */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MemberAvatar
            name={name}
            email={email}
            photoURL={userPhotoUrl}
            size={36}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {name}
            </Typography>
            {/* Only when it ADDS something (AGL-2486). With no profile name
                the line above falls back to the address, and printing it
                again underneath rendered the same address twice — which is
                what a missing name looks like, and what sent this bug
                hunting in the wrong place. */}
            {email && email !== name ? (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {email}
              </Typography>
            ) : null}
          </Box>
          <Tooltip title="Manage Account">
            <IconButton
              size="small"
              component={AppLink}
              href={Route.MANAGE_USER_SETTINGS}
              onClick={close}
              aria-label="Manage Account"
            >
              <MdiIcon path={ICON_VARIANT_USER_SETTINGS.path} fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        {/* Account-scoped, so it works from ANY page including the org-less
            ones where every row below is hidden — otherwise the workspace
            chooser and the staff console offer nothing but Documentation and
            Sign out. The header gear already links here; this is the same
            destination as a labelled row, because a gear is not discoverable
            as "Manage account". */}
        {row(
          Route.MANAGE_USER_SETTINGS,
          'Manage account',
          ICON_VARIANT_USER_SETTINGS.path,
        )}
        {showOrgLinks ? (
          <>
            {row(orgLink(Route.MANAGE_TEAM), 'Manage Team', mdiAccountGroupOutline.path)}
            {row(orgLink(Route.MANAGE_BILLING), 'Billing', mdiCreditCardOutline.path)}
            {row(orgLink(Route.MANAGE_SUPPORT), 'Support', mdiLifebuoy.path)}
          </>
        ) : null}
        {isStaff
          ? row(Route.ADMIN_OVERVIEW, 'Staff console', ICON_VARIANT_SYMBOL_SECURE.path)
          : null}
        {row(buildDocsUrl(), 'Documentation', mdiOpenInNew.path, { external: true })}

        {/* Account-scoped like Manage account and Documentation, so it needs
            neither the org gate nor the collaborator gate: anyone signed in
            can hit a bug, on any page, including the org-less ones. Placed
            under Documentation because "the docs did not answer it" is the
            step immediately before reporting it. */}
        {actionRow(
          () => {
            close()
            setReportMounted(true)
            setReporting(true)
          },
          'Report an issue',
          mdiBugOutline.path,
        )}

        {/* The console's persistent privacy control (AGL-1498 posture applied
            to this surface). A published customer site carries a fixed pill on
            every page because it has no account menu to put this in; the
            console has one, and a signed-in person looks for their own
            settings here rather than in a floating widget.

            Account-scoped like the two rows above, so no org gate and no
            collaborator gate: the choice is the person's, on any page,
            including the org-less ones.

            The label is `CONSENT_OPT_OUT_TITLE`, imported rather than typed
            out. Those exact words are what CCPA regs §7015 permit a single
            combined opt-out control to be titled, and a second copy of a
            regulated string is a second thing to keep correct.

            An event rather than local state, for the AGL-2185 reason the
            report dialog is mounted outside this Popover: the menu closes on
            click, so a panel owned here would be torn down with it. The panel
            lives in `VisitorConsent`, mounted beside the app. */}
        {actionRow(
          () => {
            close()
            openVisitorConsentPanel()
          },
          CONSENT_OPT_OUT_TITLE,
          mdiCookieCogOutline.path,
        )}

        {/* Inline theme toggle (moved out of the top bar). */}
        <Box
          sx={{
            px: 2,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {'Theme'}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={themeValue}
            onChange={(_event, value) => {
              if (value) setMode(value)
            }}
            aria-label="theme mode"
          >
            <ToggleButton value="light" aria-label="light" sx={{ px: 1, py: 0.25 }}>
              <MdiIcon path={ICON_VARIANT_THEME_LIGHT.path} fontSize="small" />
            </ToggleButton>
            <ToggleButton value="system" aria-label="system" sx={{ px: 1, py: 0.25 }}>
              <MdiIcon path={ICON_VARIANT_THEME_SYSTEM.path} fontSize="small" />
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark" sx={{ px: 1, py: 0.25 }}>
              <MdiIcon path={ICON_VARIANT_THEME_DARK.path} fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {showUpgrade ? (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Button
              fullWidth
              variant="contained"
              component={AppLink}
              href={orgLink(Route.MANAGE_BILLING)}
              onClick={close}
              sx={{
                bgcolor: 'text.primary',
                color: 'background.paper',
                '&:hover': { bgcolor: 'text.secondary' },
              }}
            >
              {'Upgrade plan'}
            </Button>
          </Box>
        ) : null}

        <Divider />
        {row(Route.AUTH_SIGN_OUT, 'Sign out', ICON_VARIANT_SIGN_OUT.path)}

        {namesOrg && currentOrg?.orgName ? (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {currentOrg.orgName}
              {/* One label source (AGL-1118) — a `plan: 'enterprise'` org
                  reads "Enterprise plan" here without a special case, and a
                  legacy custom-priced org is relabelled the same way. */}
              {planLabel ? ` · ${planLabel} plan` : ''}
            </Typography>
          </Box>
        ) : null}
      </Popover>

      {/* Outside the Popover on purpose (AGL-2185): the menu closes the moment
          the row is clicked, and a dialog mounted inside it would be torn down
          with it — taking a half-written report along.

          Nothing renders until the row is clicked, which is what keeps the
          lazy chunk off every other console page. `null` while it loads: the
          person clicked a menu row and the menu has already closed, so the
          only frames without a dialog are the ones the fetch takes. */}
      {reportMounted ? (
        <Suspense fallback={null}>
          <ReportIssueDialog
            open={reporting}
            onClose={() => setReporting(false)}
          />
        </Suspense>
      ) : null}
    </>
  )
}
UserMenu.displayName = 'UserMenu'
UserMenu.aglyn = true

export default UserMenu
